import React from 'react';
import { Box } from '@mui/material';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Autoplay, Pagination } from 'swiper/modules';
import 'swiper/css/pagination';
import {
  ImageSlide,
  SNB_BANNER_FRAME_SX,
} from '@/components/Social/@components/SnbBannerCarousel';
import { useGTMEventTracker } from '@/hooks/gtm/useGTMEventTracker';
import { EventPageAreaType, HomeEvent, WorkEvent } from '@/constant/gtm';
import { ContentBanner } from '@/types/event';
import { resolveEventBannerLink } from '@/utils/eventBannerLink';
import { AUTO_SLIDE_INTERVAL } from '@/components/Home/EventBanner';

type P = {
  banners: ContentBanner[];
  /** 포탈 게시글 주소 배너를 눌렀을 때 게시글 모달을 연다. */
  onBoardClick: (documentId: string) => void;
  /** 클릭 이벤트를 적재할 GA page_area */
  eventPageArea: EventPageAreaType;
  /** 클릭 이벤트의 area_name */
  eventAreaName: string;
  /** 클릭 이벤트명. 배너가 놓인 화면 기준으로 맞춘다. */
  eventName: HomeEvent | WorkEvent;
};

/**
 * 홈 TIP · 회의록 필터 영역 배너 캐러셀. 어드민에 등록한 배너를 노출 순서대로 보여 준다.
 * 2건 이상이면 이벤트 배너처럼 자동으로 넘기며 순환하고, 마우스를 올려 둔 동안에는 멈춘다.
 */
const ContentBannerCarousel = ({
  banners,
  onBoardClick,
  eventPageArea,
  eventAreaName,
  eventName,
}: P) => {
  const eventTracker = useGTMEventTracker<HomeEvent | WorkEvent>(eventPageArea);
  const isCarousel = banners.length > 1;

  const handleClick = (banner: ContentBanner) => {
    eventTracker(eventName, {
      area_name: eventAreaName,
      // 노출용 응답에는 배너명이 없어 배너 고유번호로 구분한다.
      contents: banner.id,
      status: undefined,
      description: 'SNB 배너',
    });

    // 포탈 게시글은 페이지를 떠나지 않도록 게시글 모달로, 그 밖은 새 창으로 연다.
    const target = resolveEventBannerLink(banner.link, window.location.origin);
    if (target.kind === 'board') {
      onBoardClick(target.documentId);
      return;
    }
    window.open(banner.link, '_blank', 'noopener,noreferrer');
  };

  return (
    <Box sx={SNB_BANNER_FRAME_SX}>
      <Swiper
        // 재조회로 배너 구성이 바뀌면 순환/자동 전환 설정을 새로 잡도록 다시 만든다.
        key={banners.map((banner) => banner.id).join()}
        slidesPerView={1}
        spaceBetween={0}
        simulateTouch={true}
        grabCursor={true}
        loop={isCarousel}
        modules={[Pagination, Autoplay]}
        pagination={isCarousel ? { clickable: true } : false}
        autoplay={
          isCarousel
            ? {
                delay: AUTO_SLIDE_INTERVAL,
                disableOnInteraction: false,
                // 실제 마우스일 때만 멈춘다(터치 탭으로는 멈추지 않음).
                pauseOnMouseEnter: true,
              }
            : false
        }
      >
        {banners.map((banner) => (
          <SwiperSlide key={banner.id}>
            <ImageSlide
              src={banner.fileUrl}
              alt={'배너'}
              onClick={() => handleClick(banner)}
            />
          </SwiperSlide>
        ))}
      </Swiper>
    </Box>
  );
};

export default ContentBannerCarousel;
